import React from 'react'
import { Preview, Section, Row } from 'component-catalog/previews/PreviewKit'
import { create } from 'zustand'
import { SelectFilter } from 'components/Filters/fields/SelectFilter'
import { SingleSelectFilter } from 'components/Filters/fields/SingleSelectFilter'
import { RangeFilter } from 'components/Filters/fields/RangeFilter'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import { ZipcodeFilter } from 'components/Filters/fields/ZipcodeFilter'
import { QuickRangeFilter } from 'components/Filters/fields/QuickFilter'
import type { FilterState, FilterStore } from 'components/Filters/FilterStore'

// Minimal in-memory FilterStore — no URL/localStorage persistence
function createDemoStore(): FilterStore {
  return create<FilterState>((set) => ({
    filterTitles: {},
    filterPrefixes: {},
    filterDecimalScales: {},
    zipCodeFilter: ['', 0],
    pendingZipCodeFilter: ['', 0],
    valueFilters: {},
    pendingValueFilters: {},
    rangeFilters: {},
    pendingRangeFilters: {},
    criteria: 'all',
    pendingCriteria: 'all',
    page: 1,
    search: {},
    pendingSearch: {},
    filter_id: undefined,

    registerFilterTitle: (key, title) =>
      set((s) => ({ filterTitles: { ...s.filterTitles, [key]: title } })),
    registerFilterPrefix: (key, prefix) =>
      set((s) => ({
        filterPrefixes: { ...s.filterPrefixes, [key]: prefix },
      })),
    registerFilterDecimalScale: (key, decimalScale) =>
      set((s) => ({
        filterDecimalScales: {
          ...s.filterDecimalScales,
          [key]: decimalScale,
        },
      })),

    setPendingValueFilters: (key, values) =>
      set((s) => {
        const pending = { ...s.pendingValueFilters, [key]: values }
        if (!values.length) delete pending[key]
        return { pendingValueFilters: pending }
      }),
    clearPendingValueFilter: (key) =>
      set((s) => {
        const pending = { ...s.pendingValueFilters }
        delete pending[key]
        return { pendingValueFilters: pending }
      }),

    setPendingRangeFilters: (key, min, max) =>
      set((s) => ({
        pendingRangeFilters: { ...s.pendingRangeFilters, [key]: { min, max } },
      })),
    clearPendingRangeFilter: (key) =>
      set((s) => {
        const pending = { ...s.pendingRangeFilters }
        delete pending[key]
        return { pendingRangeFilters: pending }
      }),

    setPendingZipCodeFilter: (zipcode, mileRadius) =>
      set({ pendingZipCodeFilter: [zipcode, mileRadius] }),
    clearPendingZipCodeFilter: () => set({ pendingZipCodeFilter: ['', 0] }),

    setPendingCriteria: (criteria) => set({ pendingCriteria: criteria }),

    clearAllPendingFilters: () =>
      set({
        pendingValueFilters: {},
        pendingRangeFilters: {},
        pendingZipCodeFilter: ['', 0],
        pendingCriteria: 'all',
      }),

    clearValueFilter: (key) =>
      set((s) => {
        const v = { ...s.valueFilters }
        delete v[key]
        return { valueFilters: v }
      }),
    clearZipCodeFilter: () => set({ zipCodeFilter: ['', 0] }),
    clearRangeFilter: (key) =>
      set((s) => {
        const r = { ...s.rangeFilters }
        delete r[key]
        return { rangeFilters: r }
      }),

    applyFilters: async () =>
      set((s) => ({
        valueFilters: s.pendingValueFilters,
        rangeFilters: s.pendingRangeFilters,
        zipCodeFilter: s.pendingZipCodeFilter,
        criteria: s.pendingCriteria,
        search: s.pendingSearch,
        page: 1,
      })),

    clearAllFilters: () =>
      set({
        valueFilters: {},
        pendingValueFilters: {},
        rangeFilters: {},
        pendingRangeFilters: {},
        zipCodeFilter: ['', 0],
        pendingZipCodeFilter: ['', 0],
        criteria: 'all',
        pendingCriteria: 'all',
        search: {},
        page: 1,
        filter_id: undefined,
      }),

    setSearch: (key, value, label, elastic = false) =>
      set((s) => ({
        search: value
          ? { ...s.search, [key]: { value, label, elastic: elastic ?? false } }
          : {},
      })),
    clearSearch: (key) =>
      set((s) => {
        const s2 = { ...s.search }
        delete s2[key]
        return { search: s2 }
      }),
    clearPendingSearch: (key) =>
      set((s) => {
        const s2 = { ...s.pendingSearch }
        delete s2[key]
        return { pendingSearch: s2 }
      }),

    setPage: (page) => set({ page }),

    loadSavedFilter: (filters) =>
      set({
        pendingValueFilters: filters.valueFilters,
        pendingRangeFilters: filters.rangeFilters,
        pendingZipCodeFilter: filters.zipCodeFilter,
        pendingCriteria: filters.criteria,
        pendingSearch: filters.search,
      }),

    syncPendingFilters: () =>
      set((s) => ({
        pendingValueFilters: s.valueFilters,
        pendingRangeFilters: s.rangeFilters,
        pendingZipCodeFilter: s.zipCodeFilter,
        pendingCriteria: s.criteria,
        pendingSearch: s.search,
      })),
  }))
}

const demoStore = createDemoStore()

const stageOptions = [
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'closed_won', label: 'Closed Won' },
]

const typeOptions = [
  { value: 'chain', label: 'Chain' },
  { value: 'independent', label: 'Independent' },
  { value: 'franchise', label: 'Franchise' },
]

const criteriaOptions = [
  { value: 'all', label: 'All' },
  { value: 'any', label: 'Any' },
]

export function FiltersMock(): React.ReactElement {
  return (
    <Preview>
      <Section label="Select Filter">
        <Row label="Multi-select dropdown — one or more values from a list">
          <SelectFilter
            filterKey="stage"
            title="Stage"
            options={stageOptions}
            filterStore={demoStore}
          />
          <SelectFilter
            filterKey="type"
            title="Type"
            options={typeOptions}
            filterStore={demoStore}
          />
        </Row>
      </Section>

      <Section label="Single Select Filter">
        <Row label="Radio-style dropdown — exactly one value">
          <SingleSelectFilter
            filterKey="criteria"
            title="Match Criteria"
            options={criteriaOptions}
            filterStore={demoStore}
          />
        </Row>
      </Section>

      <Section label="Range Filter">
        <Row label="Numeric min/max range">
          <RangeFilter title="Revenue" filterKey="revenue" store={demoStore} />
        </Row>
      </Section>

      <Section label="Date Range Filter">
        <Row label="From/to date picker">
          <DateRangeFilter
            title="Created"
            filterKey="created"
            filterStore={demoStore}
          />
        </Row>
      </Section>

      <Section label="Quick Filter">
        <Row label="Checkbox shortcut for a predefined range value">
          <QuickRangeFilter
            title="High Value"
            valueLabel="> $100k"
            filterKey="revenue"
            predefinedValue={{ min: 100000, max: undefined }}
            filterStore={demoStore}
          />
          <QuickRangeFilter
            title="Large Account"
            valueLabel="> 500 employees"
            filterKey="employees"
            predefinedValue={{ min: 500, max: undefined }}
            filterStore={demoStore}
          />
        </Row>
      </Section>

      <Section label="Zipcode Filter">
        <Row label="Zipcode + mile radius geo filter">
          <ZipcodeFilter filterStore={demoStore} />
        </Row>
      </Section>
    </Preview>
  )
}
